import React, { useState } from 'react';
import Cookies from 'js-cookie';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.jsx';
import { Button } from '../ui/button.jsx';
import { Input } from '../ui/input.jsx';
import { Label } from '../ui/label.jsx';
import { Textarea } from '../ui/textarea.jsx';
import { toast } from 'sonner';
import { 
  UserPlus, 
  Globe, 
  Target, 
  TrendingUp,
  DollarSign,
  Award,
  CheckCircle,
  Users,
  Eye,
  EyeOff
} from 'lucide-react';

const AffiliateSignup = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    // User account fields
    email: '',
    password: '',
    full_name: '',
    company_name: '',
    
    // Affiliate fields
    business_name: '',
    website: '',
    social_media: {
      linkedin: '',
      twitter: '',
      facebook: '',
      instagram: ''
    },
    target_audience: '',
    marketing_experience: '',
    referral_cookie_duration: 30
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.email || !formData.password || !formData.full_name) {
        throw new Error('Please fill in all required fields');
      }

      if (formData.password.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/affiliates/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const result = await response.json();
        
        // Store the authentication token in cookies (consistent with rest of app)
        Cookies.set('enakid_token', result.access_token, { expires: 7 });
        Cookies.set('enakid_user', JSON.stringify(result.user), { expires: 7 });
        
        toast.success('Account created successfully! Welcome to the TavPlans Affiliate Program!');
        
        // Redirect to affiliate dashboard
        setTimeout(() => {
          window.location.href = '/affiliate';
        }, 2000);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create account');
      }
    } catch (error) {
      console.error('Error creating account:', error);
      toast.error(error.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <img 
          src="/TavPlans_Logo.png" 
          alt="TavPlans Logo" 
          className="h-20 w-20 object-contain mx-auto mb-4"
        />
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Join the TavPlans Affiliate Program
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Create your account and start earning commissions by referring businesses to TavPlans' 
          powerful financial modeling platform.
        </p>
      </div>

      {/* Benefits */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="text-center">
          <CardContent className="pt-6">
            <DollarSign className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="font-semibold text-lg mb-2">High Commissions</h3>
            <p className="text-sm text-gray-600">
              Earn 20-35% recurring revenue share based on your performance tier
            </p>
          </CardContent>
        </Card>

        <Card className="text-center">
          <CardContent className="pt-6">
            <Award className="h-12 w-12 text-purple-500 mx-auto mb-4" />
            <h3 className="font-semibold text-lg mb-2">Tier System</h3>
            <p className="text-sm text-gray-600">
              Bronze to Platinum tiers with increasing commission rates and bonuses
            </p>
          </CardContent>
        </Card>

        <Card className="text-center">
          <CardContent className="pt-6">
            <TrendingUp className="h-12 w-12 text-blue-500 mx-auto mb-4" />
            <h3 className="font-semibold text-lg mb-2">Real-time Tracking</h3>
            <p className="text-sm text-gray-600">
              Comprehensive analytics dashboard with detailed performance metrics
            </p>
          </CardContent>
        </Card>

        <Card className="text-center">
          <CardContent className="pt-6">
            <Users className="h-12 w-12 text-orange-500 mx-auto mb-4" />
            <h3 className="font-semibold text-lg mb-2">Marketing Support</h3>
            <p className="text-sm text-gray-600">
              Access to marketing materials, landing pages, and promotional content
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Registration Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl flex items-center">
            <UserPlus className="h-5 w-5 mr-2" />
            Create Your Affiliate Account
          </CardTitle>
          <CardDescription>
            Sign up for TavPlans and join our affiliate program in one simple step
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Account Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Account Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input
                    id="full_name"
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => handleInputChange('full_name', e.target.value)}
                    placeholder="John Doe"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="john@example.com"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="password">Password *</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder="At least 6 characters"
                      required
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => handleInputChange('company_name', e.target.value)}
                    placeholder="Your company (optional)"
                  />
                </div>
              </div>
            </div>

            {/* Business Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Business Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="business_name">Business/Brand Name</Label>
                  <Input
                    id="business_name"
                    type="text"
                    value={formData.business_name}
                    onChange={(e) => handleInputChange('business_name', e.target.value)}
                    placeholder="Your business name (optional)"
                  />
                </div>
                
                <div>
                  <Label htmlFor="website">Website URL</Label>
                  <Input
                    id="website"
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="https://yourwebsite.com (optional)"
                  />
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center">
                <Globe className="h-5 w-5 mr-2" />
                Social Media Presence (Optional)
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="linkedin">LinkedIn Profile</Label>
                  <Input
                    id="linkedin"
                    type="url"
                    value={formData.social_media.linkedin}
                    onChange={(e) => handleInputChange('social_media.linkedin', e.target.value)}
                    placeholder="https://linkedin.com/in/yourprofile"
                  />
                </div>
                
                <div>
                  <Label htmlFor="twitter">Twitter/X Profile</Label>
                  <Input
                    id="twitter"
                    type="url"
                    value={formData.social_media.twitter}
                    onChange={(e) => handleInputChange('social_media.twitter', e.target.value)}
                    placeholder="https://twitter.com/yourhandle"
                  />
                </div>
                
                <div>
                  <Label htmlFor="facebook">Facebook Page</Label>
                  <Input
                    id="facebook"
                    type="url"
                    value={formData.social_media.facebook}
                    onChange={(e) => handleInputChange('social_media.facebook', e.target.value)}
                    placeholder="https://facebook.com/yourpage"
                  />
                </div>
                
                <div>
                  <Label htmlFor="instagram">Instagram Profile</Label>
                  <Input
                    id="instagram"
                    type="url"
                    value={formData.social_media.instagram}
                    onChange={(e) => handleInputChange('social_media.instagram', e.target.value)}
                    placeholder="https://instagram.com/yourprofile"
                  />
                </div>
              </div>
            </div>

            {/* Marketing Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center">
                <Target className="h-5 w-5 mr-2" />
                Marketing Information (Optional)
              </h3>
              
              <div>
                <Label htmlFor="target_audience">Target Audience</Label>
                <Textarea
                  id="target_audience"
                  value={formData.target_audience}
                  onChange={(e) => handleInputChange('target_audience', e.target.value)}
                  placeholder="Describe your target audience (e.g., small business owners, startups, consultants, etc.)"
                  rows={3}
                />
              </div>
              
              <div>
                <Label htmlFor="marketing_experience">Marketing Experience</Label>
                <Textarea
                  id="marketing_experience"
                  value={formData.marketing_experience}
                  onChange={(e) => handleInputChange('marketing_experience', e.target.value)}
                  placeholder="Tell us about your marketing experience and how you plan to promote TavPlans"
                  rows={4}
                />
              </div>
            </div>

            {/* Terms and Submit */}
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="text-blue-800 font-medium mb-1">What happens next?</p>
                    <ul className="text-blue-700 space-y-1">
                      <li>• Your account will be created immediately</li>
                      <li>• Your affiliate application will be reviewed within 1-2 business days</li>
                      <li>• Once approved, you'll receive your unique affiliate code</li>
                      <li>• Start earning commissions from your first referral</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
                size="lg"
              >
                {loading ? 'Creating Account...' : 'Create Account & Join Program'}
              </Button>
            </div>

            <div className="text-center text-sm text-gray-600">
              Already have an account?{' '}
              <a href="/affiliate/register" className="text-blue-600 hover:underline">
                Apply to become an affiliate
              </a>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AffiliateSignup;